import { graphHooks } from '@tunasong/graph-lib/react'
import { shortUuid } from '@tunasong/models'
import { useAgentDefinitions } from '@tunasong/plugin-lib'
import { profilesApi } from '@tunasong/redux'
import { isFolderContainer } from '@tunasong/schemas'
import type { Condition, Entity, Persisted } from '@tunasong/schemas'
import {
  Button,
  RuleCard,
  RuleEditor,
  Select,
  addCondition,
  createRule,
  isInboxRule,
  readRules,
  removeCondition,
  removeRule,
  resumeRule,
  setActionArg,
  setRuleAction,
  setRuleEnabled,
  setRuleEvent,
  setRuleTag,
  setRuleType,
  starterInboxRule,
  upsertRule,
} from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useRef, useState } from 'react'
import { EntitySelectDialog } from '../entity/entity-select-dialog.js'
import styles from './inbox-automations.module.css'

/**
 * Frontend availability gate for rule actions.
 *
 * Now true: `INBOX_RULES_ENABLED` is wired in CDK and deployed to
 * `tunasong-graph-events`, and all three server handlers
 * (`convert-entity-to-song`, `move-entity`, `link-agent`) are registered on the
 * stream path. Rules a user switches on will actually run.
 *
 * This stayed false while the engine was inert so the switch never claimed a
 * rule was running when nothing would act on it. Set it back to false to stop
 * offering the switch if the engine is ever turned off again — the builder
 * itself stays fully usable either way, since composing a rule is safe and only
 * enabling it is consequential.
 */
const INBOX_ACTION_AVAILABLE = true

/** A fresh condition of the given kind, in the shape the editor expects for an empty control. */
const defaultConditionFor = (kind: Condition['kind']): Condition => {
  if (kind === 'tag') {
    return { kind: 'tag', tag: '' }
  }
  if (kind === 'space') {
    return { kind: 'space', spaceId: '' }
  }
  return { kind: 'type', entityType: 'note' }
}

interface EntityPickerFieldProps {
  purpose: 'move-target' | 'space'
  value: string | null | undefined
  onChange: (id: string | null) => void
  /** Disambiguates this button from another rule's on the same page. See `RuleEditor`'s `ruleLabel`. */
  ruleLabel?: string
}

/**
 * The move-destination and space pickers are the same control, filtered
 * differently: both point at folder-like containers, one for where an entity
 * lands, the other for which space a rule is scoped to.
 *
 * The dialog's dummy root selection (`id: 'ROOT'`) is this field's only way to
 * express "no parent" — it is translated to `null` here so the host and the
 * schema never see the dialog's internal sentinel. `includeRoot` is what
 * makes that root item selectable at all (it is opt-in on the shared dialog);
 * without it the Inbox (`parentId: null`) could never be picked as a move
 * destination.
 */
const EntityPickerField: FC<EntityPickerFieldProps> = ({ purpose, value, onChange, ruleLabel }) => {
  const [open, setOpen] = useState(false)
  const { entity } = graphHooks.useEntity(value)
  const title = purpose === 'space' ? 'Select a space' : 'Select destination'
  const buttonLabel = ruleLabel ? `${title} (${ruleLabel})` : title
  const label = value === undefined ? 'Choose…' : value === null ? 'Inbox' : (entity?.name ?? 'Choose…')
  // "Top" is the same tree item in both dialogs, but it means something
  // different in each: the Inbox for a move destination, no filter at all
  // for a scope filter. Spelling that out here is what keeps the two from
  // reading as the same unexplained control.
  const description =
    purpose === 'space'
      ? 'Choose Top to clear this filter — the rule will match every space.'
      : 'Choose Top for the Inbox.'

  const handleClose = (selected?: Persisted<Entity>) => {
    setOpen(false)
    if (!selected) {
      return
    }
    onChange(selected.id === 'ROOT' ? null : selected.id)
  }

  return (
    <span className={styles.picker}>
      <Button aria-label={buttonLabel} onClick={() => setOpen(true)}>
        {label}
      </Button>
      <EntitySelectDialog
        open={open}
        includeRoot
        filter={isFolderContainer}
        title={title}
        description={description}
        onClose={handleClose}
      />
    </span>
  )
}

export const InboxAutomations: FC = () => {
  const { profile } = graphHooks.useProfile()
  const [updateProfile] = profilesApi.useUpdateProfileMutation()
  const agentDefinitions = useAgentDefinitions()
  const config = profile?.config
  const inboxRules = readRules(config).filter(isInboxRule)

  /**
   * Every mutator below composes its next config from "the current one". The
   * panel now has eight immediate-save controls per rule, so two can fire in
   * the same tick -- e.g. a tag input's blur-commit while a click on another
   * control resolves right after. Both would read the same `config` snapshot
   * captured at render time if `save` closed over it directly, so the second
   * write would not build on the first and would silently undo it. The ref is
   * updated the instant a save is composed, so the next same-tick save reads
   * what was *just* written rather than what this render started with.
   */
  const configRef = useRef(config)
  configRef.current = config

  const save = (mutate: (current: Record<string, unknown> | undefined) => Record<string, unknown>) => {
    if (!profile) {
      return
    }
    const next = mutate(configRef.current)
    configRef.current = next
    void updateProfile({ id: profile.id, profile: { config: next } })
  }

  const handleAddAutomation = () => save(current => upsertRule(current, createRule(shortUuid())))

  const handleSpaceChange = (ruleId: string, spaceId: string | null) => {
    // A rule's space condition is never null in storage (the schema requires a
    // string) -- picking "Inbox"/top-level for a scope filter means "no space
    // filter at all", so it clears the condition rather than writing an invalid one.
    if (spaceId === null) {
      save(current => {
        const target = readRules(current).find(rule => rule.id === ruleId)
        const spaceIndex = target?.conditions.findIndex(condition => condition.kind === 'space') ?? -1
        return spaceIndex === -1 ? (current ?? {}) : removeCondition(current, ruleId, spaceIndex)
      })
      return
    }
    save(current => addCondition(current, ruleId, { kind: 'space', spaceId }))
  }

  return (
    <section className={styles.section} aria-label="Inbox automations">
      <header className={styles.header}>
        <h3 className={styles.heading}>Inbox</h3>
        <p className={styles.sub}>Automate what happens to things you capture.</p>
      </header>

      {inboxRules.length === 0 ? (
        <div className={styles.starter} role="group" aria-label="Turn tagged notes into Songs">
          <div>
            <strong>Turn tagged notes into Songs</strong>
            <p className={styles.sub}>When you tag a note song, convert it to a Song automatically.</p>
          </div>
          <Button onClick={() => save(current => upsertRule(current, starterInboxRule()))}>Set up</Button>
        </div>
      ) : (
        <>
          {inboxRules.map((rule, index) => {
            // Only qualify when there is something to disambiguate -- a single
            // rule's controls need no "(automation 1)" suffix cluttering them.
            const ruleLabel = inboxRules.length > 1 ? `automation ${index + 1}` : undefined
            const renderEntityPicker = (pickerProps: EntityPickerFieldProps) => (
              <EntityPickerField {...pickerProps} ruleLabel={ruleLabel} />
            )
            const renderAgentPicker = ({
              value,
              onChange,
            }: {
              value: string | undefined
              onChange: (definitionId: string) => void
            }) => (
              <Select
                aria-label={ruleLabel ? `Agent (${ruleLabel})` : 'Agent'}
                value={value ?? ''}
                onChange={event => onChange(event.target.value)}
              >
                <option value="">Select an agent</option>
                {agentDefinitions.map(definition => (
                  <option key={definition.id} value={definition.id}>
                    {definition.name}
                  </option>
                ))}
              </Select>
            )

            return (
              <RuleCard
                key={rule.id}
                rule={rule}
                ruleLabel={ruleLabel}
                actionAvailable={INBOX_ACTION_AVAILABLE}
                editor={
                  <RuleEditor
                    rule={rule}
                    ruleLabel={ruleLabel}
                    onEventChange={event => save(current => setRuleEvent(current, rule.id, event))}
                    onTypeChange={entityType => save(current => setRuleType(current, rule.id, entityType))}
                    onAddCondition={kind => save(current => addCondition(current, rule.id, defaultConditionFor(kind)))}
                    onRemoveCondition={index => save(current => removeCondition(current, rule.id, index))}
                    onTagChange={(index, tag) => save(current => setRuleTag(current, rule.id, index, tag))}
                    onActionChange={commandId => save(current => setRuleAction(current, rule.id, commandId))}
                    onActionArgChange={(name, value) => save(current => setActionArg(current, rule.id, name, value))}
                    onSpaceChange={spaceId => handleSpaceChange(rule.id, spaceId)}
                    renderEntityPicker={renderEntityPicker}
                    renderAgentPicker={renderAgentPicker}
                  />
                }
                onToggle={enabled => save(current => setRuleEnabled(current, rule.id, enabled))}
                onResume={() => save(current => resumeRule(current, rule.id))}
                onDelete={() => save(current => removeRule(current, rule.id))}
              />
            )
          })}
          <Button onClick={handleAddAutomation}>Add automation</Button>
        </>
      )}
    </section>
  )
}
